/*
 * File: CoreEditor.tsx
 * Author: Austin Robinson
 * Date Created: 1/28/2025
 * Copyright (c) DrivingDoc 2025
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { CLEAR_HISTORY_COMMAND, type SerializedEditorState } from 'lexical';

import ParagraphSkeleton from 'components/Skeletons/ParagraphSkeleton';

import { PlaceholderPlugin, ToolbarPlugin } from '../plugins';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import MergeTagPlugin from '../plugins/MergeTagPlugin';
import { ContentEditable } from '../shared';

/**
 * Props for the CoreEditor component.
 *
 * @interface CoreEditorProps
 */
export interface CoreEditorProps {
  /**
   * The initial serialized state of the editor.
   * Loaded into the editor on mount via useEffect.
   *
   * @type {SerializedEditorState}
   */
  initialState?: SerializedEditorState;

  /**
   * Placeholder text shown when the editor is empty.
   *
   * @type {string}
   */
  placeholder: string;

  /**
   * Whether the editor is in an error state.
   * Affects border color styling.
   *
   * @type {boolean}
   */
  error: boolean;

  /**
   * Whether the editor is disabled.
   * Reduces opacity and prevents interaction.
   *
   * @type {boolean}
   */
  disabled: boolean;

  /**
   * Minimum height of the editor in pixels.
   *
   * @type {number}
   */
  minHeight: number;

  /**
   * Whether the editor is loading.
   * Shows skeleton loader when true.
   *
   * @type {boolean}
   */
  isLoading: boolean;

  /**
   * Merge tags available for insertion into the editor.
   *
   * @type {MergeTags}
   */
  mergeTags?: MergeTags;

  /**
   * Whether to show the save button in the toolbar.
   *
   * @type {boolean}
   */
  showSaveButton: boolean;

  /**
   * Text for the save button.
   *
   * @type {string}
   */
  saveButtonText?: string;

  /**
   * Icon for the save button.
   *
   * @type {ReactNode}
   */
  saveButtonIcon?: ReactNode;

  /**
   * Whether the save operation is in progress.
   * Disables the save button when true.
   *
   * @type {boolean}
   */
  isSaving: boolean;

  /**
   * Callback triggered when the editor state changes.
   * Receives the new serialized editor state and whether the editor has unsaved changes.
   *
   * @param {SerializedEditorState} state - The new editor state.
   * @param {boolean} isDirty - Whether the editor has unsaved changes.
   * @returns {void}
   */
  onChange?: (state: SerializedEditorState, isDirty: boolean) => void;

  /**
   * Callback triggered when the save button is clicked.
   * Receives the current editor state.
   *
   * @param {SerializedEditorState | undefined} state - The current editor state.
   * @returns {void | Promise<void>}
   */
  onSave?: (state: SerializedEditorState | undefined) => void | Promise<void>;
}

/**
 * Core editor component that has access to Lexical context.
 * Handles initial state loading, dirty tracking, toolbar, and editor content.
 */
export function CoreEditor({
  initialState,
  placeholder,
  error,
  disabled,
  minHeight,
  isLoading,
  mergeTags,
  showSaveButton,
  saveButtonText,
  saveButtonIcon,
  isSaving,
  onChange,
  onSave,
}: CoreEditorProps) {
  const [editor] = useLexicalComposerContext();
  const hasInitialized = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  // Load initial state into editor once
  useEffect(() => {
    if (!hasInitialized.current) {
      if (initialState) {
        const parsed = editor.parseEditorState(initialState);
        editor.update(() => {
          editor.setEditorState(parsed);

          // remove initialization from history
          editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
        });
        setIsDirty(false);
      }

      // Delay setting hasInitialized to avoid catching the initialization onChange
      setTimeout(() => {
        hasInitialized.current = true;
      }, 0);
    }
  }, [initialState, editor]);

  // Handle editor state changes
  const handleChange = useCallback(
    (state: SerializedEditorState) => {
      if (hasInitialized.current) {
        console.log('Editor is now dirty');
        setIsDirty(true);
      }
      onChange?.(state, isDirty);
    },
    [onChange, isDirty],
  );

  // Handle save button click
  const handleSave = useCallback(async () => {
    // Get current state directly from editor
    const currentState = editor.getEditorState().toJSON();

    // Call the onSave callback (parent handles success/error feedback)
    const result = onSave?.(currentState);
    setIsDirty(false);

    // If onSave returns a promise, wait for it and reset dirty state on success
    if (result instanceof Promise) {
      try {
        await result;
        // Reset dirty flag after successful save
        setIsDirty(false);
      } catch {
        // Parent handles error display, keep dirty state so user can retry
      }
    }
  }, [editor, onSave]);

  return (
    <>
      {/* Toolbar section - contains formatting controls and optional save button */}
      <Box
        sx={{
          // Border styling
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider', // Red border when error state
          // Rounded corners only on top (connects to editor below)
          borderRadius: '8px 8px 0 0',
          // Padding around toolbar content
          p: 1,
          // Light gray background to visually separate from editor content
          backgroundColor: 'grey.50',
        }}
      >
        <ToolbarPlugin editorType="rich">
          {/* Optional save button - only shown if showSaveButton is true */}
          {showSaveButton && (
            <Stack direction="row" justifyContent="flex-end" flexGrow={1} spacing={1}>
              <Button
                variant={isDirty ? 'contained' : 'outlined'}
                color={isDirty ? 'primary' : 'inherit'}
                startIcon={saveButtonIcon ?? <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || disabled || !isDirty}
              >
                {isSaving ? 'Saving...' : (saveButtonText ?? 'Save')}
              </Button>
            </Stack>
          )}
        </ToolbarPlugin>
      </Box>

      {/* Editor content area - main editable section */}
      <Paper
        variant="outlined"
        sx={{
          // Layout properties
          display: 'flex',
          flexDirection: 'column',
          // Remove top border to connect seamlessly with toolbar
          borderTop: 'none',
          // Rounded corners only on bottom (connects to toolbar above)
          borderRadius: '0 0 8px 8px',
          // Minimum height for editor (from prop)
          minHeight,

          // Error state - red border when error is true
          borderColor: error ? 'error.main' : 'divider',

          // Disabled state styling
          opacity: disabled ? 0.6 : 1, // Reduced opacity when disabled
          pointerEvents: disabled ? 'none' : 'auto', // Prevent interaction when disabled

          // Smooth border color transition for state changes
          transition: 'border-color 0.2s',

          // Focus state - mimics MUI TextField focus behavior
          '&:focus-within': {
            borderColor: error ? 'error.main' : 'primary.main', // Blue or red depending on error state
            boxShadow: (theme) =>
              error ? `0 0 0 1px ${theme.palette.error.main}` : `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        {/* Lexical rich text editor plugins */}
        <RichTextPlugin
          contentEditable={
            <>
              {!isLoading && <ContentEditable />}
              {isLoading && (
                <Box p={2}>
                  <ParagraphSkeleton paragraphs={1} />
                </Box>
              )}
            </>
          }
          aria-placeholder={placeholder}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <PlaceholderPlugin placeholder={placeholder} />
        <HistoryPlugin />
        <TabIndentationPlugin maxIndent={7} />
        <ListPlugin />
        <CheckListPlugin />
        <AutoLinkPlugin />
        {mergeTags && <MergeTagPlugin mergeTags={mergeTags} />}
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState) => {
            editorState.read(() => {
              const json = editorState.toJSON();
              handleChange(json);
            });
          }}
        />
      </Paper>
    </>
  );
}

export default CoreEditor;
